'use client'

import Image from 'next/image'
import Pill from '@/components/ui/Pill'
import Link from '@/components/ui/Link'
import AppIcon from '@/components/content/app/AppIcon'
import CriteriaIcons from '@/components/ui/CriteriaIcons'
import Badge from '@/components/ui/Badge'
import Award from '@/components/ui/Award'
import RecommendationBadge from '@/components/ui/RecommendationBadge'
import Menu from '@/components/ui/Menu'
import { FeaturedPlacement, trackFeaturedClick } from '@/lib/analytics'
import { cn, hexToRgba, getBrandColors } from '@/lib/utils'
import { cva } from 'class-variance-authority'
import { Recommendation } from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { getAppRecommendation } from '@/components/content/app/helpers'
import { FeaturedTagType } from '@/collections/FeaturedTag/constants'
import { generateMenu } from '@/helpers/menus'
import { checkAppBranding } from '@/lib/formatting'
import type { App, Media } from '@/payload-types'

const backgroundTileVariants = cva(
  'relative h-40 w-40 rounded-[36px] border border-black/10 shadow-2xl',
  {
    variants: {
      variant: {
        default: 'z-10 bg-gradient-to-b from-white/50 to-transparent opacity-80',
        base: 'z-10 bg-gradient-to-b from-white to-white/50',
        middle:
          'z-20 bg-gradient-to-b from-white to-white/50 transition-transform duration-1000 ease-in-out group-hover:-translate-x-0 group-hover:-translate-y-0',
        top: 'z-30 transition-transform duration-1000 ease-in-out group-hover:-translate-x-0 group-hover:-translate-y-0',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

function BackgroundTile({
  variant = 'default',
  className,
  style,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { variant?: 'default' | 'base' | 'middle' | 'top' }) {
  return (
    <div className={cn(backgroundTileVariants({ variant }), className)} style={style} {...props} />
  )
}

export default function ReviewThumbnail({
  reviewApp,
  type,
  hideButton = false,
  customLink,
  placement,
}: {
  reviewApp: App
  type?: string
  hideButton?: boolean
  customLink?: string
  placement?: FeaturedPlacement
}) {
  const dealCount = reviewApp.deals?.docs?.length ?? 0
  const comparisonCount = reviewApp.comparisonsApps?.docs?.length ?? 0
  const link = customLink ?? `/apps/${reviewApp.slug}`
  const appMenu = hideButton ? generateMenu('app', { app: reviewApp }) : []

  // the categoryId 0 is intentional to not provide a badge without a category when all is selected
  const { recommendation, bestFor } = getAppRecommendation({ app: reviewApp, categoryId: 0 })

  const handleTrack = placement ? () => trackFeaturedClick(placement, reviewApp.name) : undefined

  const buttons = (
    <div className="relative z-10 flex gap-3 px-0.5">
      <Pill style="primary">Read Review</Pill>
    </div>
  )

  const { primaryColor, backgroundColor } = getBrandColors(reviewApp)
  const { appGlyph, appIcon } = checkAppBranding(reviewApp)

  switch (type) {
    case FeaturedTagType.ThumbnailPrimary:
      return (
        <Link
          href={link}
          onClick={handleTrack}
          key={reviewApp.id}
          className="group relative flex h-[300px] w-full flex-col items-start justify-center gap-5 rounded-2xl border border-black/10 bg-gradient-to-r from-white to-zinc-50 p-10"
        >
          <div className="relative z-10 flex flex-col gap-2">
            <Badge style="primary">New!</Badge>
            {criteria(false)}
            {details(false)}
          </div>
          {buttons}

          <div
            style={{
              WebkitMaskImage: 'linear-gradient(to right, rgba(0,0,0,0) 25%, rgba(0,0,0,1) 65%)',
              maskImage: 'linear-gradient(to right, rgba(0,0,0,0) 25%, rgba(0,0,0,1) 65%)',
            }}
            className="absolute left-0 top-0 z-0 h-full w-full overflow-hidden rounded-2xl"
          >
            <div className="relative right-20 top-1/2 flex w-[545px] -translate-y-[230px] translate-x-32 -rotate-[38deg] skew-x-[16deg] skew-y-12 flex-wrap gap-8 lg:right-0">
              {Array.from({ length: 4 }).map((_, i) => (
                <BackgroundTile key={`bg-tile-${i}`} />
              ))}

              <div className="relative z-20 h-40 w-40">
                <BackgroundTile variant="base" className="absolute" />
                <BackgroundTile
                  variant="middle"
                  className="absolute -translate-y-[30px] translate-x-[30px]"
                />
                <BackgroundTile
                  variant="top"
                  className="absolute -translate-y-[60px] translate-x-[60px]"
                  style={{ backgroundColor }}
                >
                  <AppIcon
                    icon={appIcon}
                    glyph={appGlyph}
                    name={reviewApp.name}
                    background={backgroundColor}
                    size={160}
                  />
                </BackgroundTile>
              </div>

              {Array.from({ length: 4 }).map((_, i) => (
                <BackgroundTile key={`bg-tile-end-${i}`} />
              ))}
            </div>
          </div>
        </Link>
      )
    case FeaturedTagType.ThumbnailSecondary: {
      const resolvedBackground = backgroundColor === '#FFFFFF' ? '#000000' : backgroundColor
      const homepageImage = reviewApp.homepageImage as Media
      const homepageImageUrl = homepageImage?.url

      return (
        <Link
          href={link}
          onClick={handleTrack}
          key={reviewApp.id}
          className="group relative flex h-[440px] flex-col items-start justify-end gap-5 rounded-2xl bg-gradient-to-t from-white to-zinc-50 p-6 shadow-icon"
          style={{
            background: `radial-gradient(70% 100% at 0% 0%, ${hexToRgba(primaryColor, 0.25)} 0%, ${hexToRgba(primaryColor, 0)} 80%), linear-gradient(180deg, rgba(0, 0, 0, 0) 25%, rgba(0, 0, 0, 0.8) 90%), ${resolvedBackground}`,
          }}
        >
          <div className="flex flex-col gap-4">
            {criteria(true)}
            {details(true)}
            {buttons}
          </div>

          <div
            style={{
              WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0) 30%, rgba(0,0,0,1) 100%)',
              maskImage: 'linear-gradient(to top, rgba(0,0,0,0) 30%, rgba(0,0,0,1) 100%)',
            }}
            className="absolute left-0 top-0 z-0 h-full w-full overflow-hidden rounded-2xl"
          >
            {/* The previous implementation used CSS `background-image` here, which
                shipped the source PNG raw (~145-320 KB each, ~680 KB total across
                the 3 ThumbnailSecondary cards on /apps). next/image routes the
                same asset through /_next/image → AVIF at ~10-15 KB per image.
                `object-left-top` matches CSS background-image's top-left anchor
                (next/image's `object-cover` defaults to center). The wrapper
                keeps its skew/rotate/shadow/hover transforms — `<Image fill>`
                follows the wrapper's transformed coordinate space. */}
            <div className="backface-hidden absolute bottom-1/4 aspect-screen h-3/4 translate-x-24 -rotate-[15deg] skew-x-12 skew-y-3 overflow-hidden rounded-[20px] bg-white shadow-[0px_50px_100px_-25px_rgba(0,0,0,1)] transition-transform duration-1000 ease-in-out after:absolute after:inset-0 after:rounded-[20px] after:border after:border-white after:border-opacity-15 group-hover:translate-x-16">
              {homepageImageUrl && (
                <Image
                  src={homepageImageUrl}
                  alt={`${reviewApp.name} homepage screenshot`}
                  fill
                  sizes="(min-width: 1024px) 33vw, 50vw"
                  className="object-cover object-left-top"
                />
              )}
            </div>
          </div>
        </Link>
      )
    }
    default:
      return (
        <div className="flex items-center justify-between gap-5">
          <Link href={link} onClick={handleTrack} className="flex w-full items-center gap-3.5">
            <AppIcon
              icon={appIcon}
              glyph={appGlyph}
              name={reviewApp.name}
              background={backgroundColor}
              size={56}
            />

            <div className="mt-1 flex flex-col gap-1">
              <div className="flex h-5 w-full items-center gap-2.5">
                <span className="line-clamp-1 font-semibold text-gray-950 dark:text-white">
                  {reviewApp.name}
                </span>
                <RecommendationBadge recommendation={recommendation} />
              </div>
              <span
                aria-hidden="true"
                className="line-clamp-1 w-full text-13 text-gray-600 dark:text-white/60"
              >
                {bestFor || reviewApp.description}
              </span>
            </div>
          </Link>

          {!hideButton ? (
            <div className="flex gap-3">
              <Link href={link} onClick={handleTrack}>
                <Pill style="outlinePrimary" className="ml-auto">
                  Read<span className="sr-only"> Review</span>
                  <span className="ml-1 hidden sm:inline" aria-hidden="true">
                    Review
                  </span>
                </Pill>
              </Link>
            </div>
          ) : appMenu.length > 0 ? (
            <Menu items={appMenu} />
          ) : null}
        </div>
      )
  }

  function details(invert: boolean) {
    return (
      <div
        className={cn('flex flex-col text-gray-950 [&>p>span]:text-gray-600 [&>p]:text-gray-400', {
          'text-white [&>p>span]:text-white/90 [&>p]:text-white/60': invert,
        })}
      >
        <span className="line-clamp-1 w-fit text-20 font-semibold">{reviewApp.name}</span>
        <p className="text-13">
          <span>
            {dealCount} deal{dealCount === 1 ? '' : 's'}
          </span>{' '}
          and {comparisonCount} comparison
          {comparisonCount === 1 ? '' : 's'}
        </p>
      </div>
    )
  }

  function criteria(invert: boolean) {
    return (
      <div className={cn('mb-1.5 mt-3 flex items-center', { 'text-white': invert })}>
        <Award
          recommendation={Recommendation.Recommended}
          className={cn('[&>svg]:h-8', { '[&>svg>path]:fill-white': invert })}
        />

        <CriteriaIcons app={reviewApp} color={invert ? 'text-white' : 'text-gray-500'} />
      </div>
    )
  }
}
